package com.schoolconnect.app.feature.schools

import com.schoolconnect.app.datasource.network.response.DashboardSchoolsAnalyticsResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState

data class SchoolsUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val isSavingSchool: Boolean = false,
    val errorMessage: String = "",
    val infoMessage: String = "",
    val searchQuery: String = "",
    val filters: SchoolFilterCriteria = SchoolFilterCriteria(),
    val sortField: SchoolTableSortField = SchoolTableSortField.NAME,
    val isSortAscending: Boolean = true,
    val schools: List<SchoolRowUi> = emptyList(),
    val schoolsAnalytics: DashboardSchoolsAnalyticsResponse? = null,
    val screenMode: SchoolsScreenMode = SchoolsScreenMode.LIST,
    val selectedSchoolId: String = "",
    val selectedSchool: SchoolRowUi? = null
) : ServerPagedListUiState

sealed interface SchoolsUiEvent {
    data object BackClicked : SchoolsUiEvent
    data object RefreshRequested : SchoolsUiEvent
    data object AddSchoolClicked : SchoolsUiEvent
    data class SearchChanged(val query: String) : SchoolsUiEvent
    data class FilterChanged(val filter: SchoolFilterCriteria) : SchoolsUiEvent
    data class SortChanged(val field: SchoolTableSortField) : SchoolsUiEvent
    data class PageChanged(val pageIndex: Int) : SchoolsUiEvent
    data class PageSizeChanged(val pageSize: Int) : SchoolsUiEvent
    data class SaveSchool(val draft: SchoolEditorDraft) : SchoolsUiEvent
    data class EditSchoolClicked(val school: SchoolRowUi) : SchoolsUiEvent
    data class ViewSchoolClicked(val school: SchoolRowUi) : SchoolsUiEvent
    data object CloseDetailView : SchoolsUiEvent
    data object CloseActiveScreen : SchoolsUiEvent
    data class ToggleSchoolStatus(val schoolId: String) : SchoolsUiEvent
    data class DeleteSchool(val schoolId: String) : SchoolsUiEvent
    data class ManageUsers(val schoolId: String) : SchoolsUiEvent
    data class ViewDetails(val schoolId: String) : SchoolsUiEvent
    data object ClearMessages : SchoolsUiEvent
}

enum class SchoolsScreenMode {
    LIST,
    ADD,
    EDIT,
    DETAILS
}

data class SchoolRowUi(
    val id: String,
    val name: String,
    val code: String,
    val city: String,
    val contactName: String,
    val students: Int?,
    val isActive: Boolean,
    val board: String = "",
    val schoolType: String = "",
    val medium: String = "",
    val location: String = "",
    val createdOn: String = "",
    val updatedOn: String = "",
    val createdBy: String = "",
    val updatedBy: String = "",
    val addressLine1: String = "",
    val addressLine2: String = "",
    val district: String = "",
    val state: String = "",
    val country: String = "",
    val pinCode: String = "",
    val schoolPhone: String = "",
    val schoolEmail: String = "",
    val primaryPhone: String = "",
    val primaryEmail: String = "",
    val secondaryContactName: String = "",
    val secondaryPhone: String = "",
    val secondaryEmail: String = "",
    val attachmentFileId: String = "",
    val schoolLogoUrl: String = "",
    val schoolDescription: String = ""
)

data class SchoolEditorDraft(
    val id: String = "",
    val name: String = "",
    val code: String = "",
    val board: String = "",
    val type: String = "",
    val medium: String = "",
    val addressLine1: String = "",
    val addressLine2: String = "",
    val city: String = "",
    val district: String = "",
    val state: String = "",
    val country: String = "",
    val pinCode: String = "",
    val schoolPhone: String = "",
    val schoolEmail: String = "",
    val contactName: String = "",
    val primaryPhone: String = "",
    val primaryEmail: String = "",
    val secondaryContactName: String = "",
    val secondaryPhone: String = "",
    val secondaryEmail: String = "",
    val schoolLogoBytes: ByteArray? = null,
    val schoolLogoFileName: String = "",
    val attachmentFileId: String = "",
    val schoolLogoUrl: String = "",
    val schoolDescription: String = "",
    val students: String = "",
    val isActive: Boolean = true
)

data class SchoolFilterCriteria(
    val status: SchoolStatusFilter = SchoolStatusFilter.ALL,
    val board: String = "",
    val schoolType: String = "",
    val medium: String = "",
    val location: String = "",
    val minStudents: String = "",
    val maxStudents: String = "",
    val createdFrom: String = "",
    val createdTo: String = "",
    val updatedFrom: String = "",
    val updatedTo: String = ""
)

internal fun SchoolFilterCriteria.hasAdvancedFilters(): Boolean =
    board.isNotBlank() || schoolType.isNotBlank() || medium.isNotBlank() ||
        minStudents.isNotBlank() || maxStudents.isNotBlank() ||
        createdFrom.isNotBlank() || createdTo.isNotBlank() ||
        updatedFrom.isNotBlank() || updatedTo.isNotBlank()

enum class SchoolStatusFilter {
    ALL,
    ACTIVE,
    INACTIVE
}

internal fun SchoolStatusFilter.displayLabel(): String = when (this) {
    SchoolStatusFilter.ALL -> "All"
    SchoolStatusFilter.ACTIVE -> "Active"
    SchoolStatusFilter.INACTIVE -> "Inactive"
}

internal fun String.toSchoolStatusFilter(): SchoolStatusFilter = when (this) {
    "Active" -> SchoolStatusFilter.ACTIVE
    "Inactive" -> SchoolStatusFilter.INACTIVE
    else -> SchoolStatusFilter.ALL
}

enum class SchoolTableSortField(val apiField: String) {
    NAME("name"),
    CODE("code"),
    CITY("city"),
    CONTACT("primary_contact_name"),
    STUDENTS("student_count"),
    STATUS("is_active")
}
